import { useEffect, useRef, useState } from 'react';
import {
  CONDICIONES_IVA,
  ETIQUETA_CONDICION_IVA,
  LOGO_MAX_BYTES,
  type CondicionIVA,
  type PerfilEmpresaDTO,
} from '@ausentia/shared';
import { api, ErrorApi } from '../api/cliente.js';
import { base64DeDataUrl } from '../almacen/cripto.js';
import { useToast } from '../componentes/Toast.js';

/** ADR-0011: la empresa edita sus datos fiscales y su logo (los usa la factura). */
export function PerfilEmpresa() {
  const toast = useToast();
  const inputLogo = useRef<HTMLInputElement>(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const [nombre, setNombre] = useState('');
  const [razonSocial, setRazonSocial] = useState('');
  const [cuit, setCuit] = useState('');
  const [condicionIva, setCondicionIva] = useState<CondicionIVA | ''>('');
  const [domicilioFiscal, setDomicilioFiscal] = useState('');
  const [emailFacturacion, setEmailFacturacion] = useState('');
  const [telefono, setTelefono] = useState('');
  const [logoBase64, setLogoBase64] = useState<string | null>(null);
  const [logoMime, setLogoMime] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<PerfilEmpresaDTO>('/api/portal/perfil')
      .then((p) => {
        setNombre(p.nombre);
        setRazonSocial(p.razonSocial ?? '');
        setCuit(p.cuit ?? '');
        setCondicionIva((p.condicionIva ?? '') as CondicionIVA | '');
        setDomicilioFiscal(p.domicilioFiscal ?? '');
        setEmailFacturacion(p.emailFacturacion ?? '');
        setTelefono(p.telefono ?? '');
        setLogoBase64(p.logoBase64);
        setLogoMime(p.logoMime);
      })
      .catch((err) => setError(err instanceof ErrorApi ? err.message : 'No se pudo cargar el perfil'))
      .finally(() => setCargando(false));
  }, []);

  function elegirLogo(e: React.ChangeEvent<HTMLInputElement>) {
    const archivo = e.target.files?.[0];
    if (!archivo) return;
    setError('');
    if (archivo.type !== 'image/png' && archivo.type !== 'image/jpeg') {
      setError('El logo debe ser PNG o JPG');
      return;
    }
    if (archivo.size > LOGO_MAX_BYTES) {
      setError(`El logo supera ${Math.round(LOGO_MAX_BYTES / 1024)} KB`);
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setLogoBase64(base64DeDataUrl(ev.target?.result as string));
      setLogoMime(archivo.type);
    };
    reader.readAsDataURL(archivo);
  }

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    setGuardando(true);
    setError('');
    try {
      await api.put('/api/portal/perfil', {
        razonSocial: razonSocial || null,
        cuit: cuit || null,
        condicionIva: condicionIva || null,
        domicilioFiscal: domicilioFiscal || null,
        emailFacturacion: emailFacturacion || null,
        telefono: telefono || null,
        logoBase64,
        logoMime,
      });
      toast.mostrar('Perfil de la empresa actualizado');
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar el perfil');
    } finally {
      setGuardando(false);
    }
  }

  if (cargando) return <div className="cargando">Cargando perfil…</div>;

  const logoSrc = logoBase64 ? `data:${logoMime ?? 'image/png'};base64,${logoBase64}` : null;

  return (
    <>
      <h2>Perfil de la empresa</h2>
      <p className="muted">
        Completá los datos fiscales que el servicio de control usa para facturarte y subí tu logo. Esta
        información aparece en los comprobantes que recibís.
      </p>
      {error && <div className="error-box">{error}</div>}

      <form className="tarjeta perfil-empresa-form" onSubmit={guardar}>
        <h3 style={{ marginTop: 0, fontSize: 14 }}>Logo</h3>
        <div className="fila" style={{ alignItems: 'center', gap: 16, marginBottom: 18, justifyContent: 'flex-start' }}>
          {logoSrc ? (
            <img src={logoSrc} alt="Logo de la empresa" className="logo-empresa" />
          ) : (
            <div className="logo-empresa logo-empresa-vacio">Sin logo</div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <button type="button" className="secundario" style={{ width: 'auto' }} onClick={() => inputLogo.current?.click()}>
              Subir logo
            </button>
            {logoBase64 && (
              <button
                type="button"
                className="fantasma"
                style={{ width: 'auto' }}
                onClick={() => { setLogoBase64(null); setLogoMime(null); }}
              >
                Quitar
              </button>
            )}
            <span className="mini muted">PNG o JPG, máx. {Math.round(LOGO_MAX_BYTES / 1024)} KB</span>
          </div>
          <input ref={inputLogo} type="file" accept="image/png,image/jpeg" style={{ display: 'none' }} onChange={elegirLogo} />
        </div>

        <h3 style={{ fontSize: 14 }}>Datos fiscales</h3>
        <div className="campo">
          <label htmlFor="pf-nombre">Nombre (lo administra el operador)</label>
          <input id="pf-nombre" type="text" value={nombre} disabled />
        </div>
        <div className="campo">
          <label htmlFor="pf-razon">Razón social</label>
          <input id="pf-razon" type="text" value={razonSocial} onChange={(e) => setRazonSocial(e.target.value)} />
        </div>
        <div className="campo">
          <label htmlFor="pf-cuit">CUIT</label>
          <input id="pf-cuit" type="text" value={cuit} onChange={(e) => setCuit(e.target.value)} placeholder="30-12345678-9" />
          <p className="mini muted" style={{ margin: '4px 0 0' }}>Formato: XX-XXXXXXXX-X</p>
        </div>
        <div className="campo">
          <label htmlFor="pf-iva">Condición frente al IVA</label>
          <select id="pf-iva" value={condicionIva} onChange={(e) => setCondicionIva(e.target.value as CondicionIVA | '')}>
            <option value="">— Seleccionar —</option>
            {CONDICIONES_IVA.map((c) => (
              <option key={c} value={c}>{ETIQUETA_CONDICION_IVA[c]}</option>
            ))}
          </select>
        </div>
        <div className="campo">
          <label htmlFor="pf-dom">Domicilio fiscal</label>
          <input id="pf-dom" type="text" value={domicilioFiscal} onChange={(e) => setDomicilioFiscal(e.target.value)} />
        </div>
        <div className="campo">
          <label htmlFor="pf-email">Email de facturación</label>
          <input id="pf-email" type="email" value={emailFacturacion} onChange={(e) => setEmailFacturacion(e.target.value)} />
        </div>
        <div className="campo">
          <label htmlFor="pf-tel">Teléfono</label>
          <input id="pf-tel" type="tel" value={telefono} onChange={(e) => setTelefono(e.target.value)} />
        </div>

        <div className="fila" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="submit" className="primario" style={{ width: 'auto' }} disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </form>
    </>
  );
}
